import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES, get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { InterviewStatus } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";
import { isAfter } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime } from "date-fns-tz";
import { isEmpty } from "lodash";
import { useHistory } from "react-router-dom";
import { z } from "zod";

import { getPlacementCardType } from "../utils";
import { type PlacementDetail } from "./useGetPlacement";

interface GetInterviewAvailabilityParams {
  workplaceId: string;
}

interface GetInterviewAvailabilityQueryParams {
  filter?: {
    workerId?: string;
    canScheduleInterviews?: boolean;
    hiringEventId?: string;
    placementId?: string;
    excludedInterviewId?: string;
    start?: {
      gte?: Date;
    };
  };
}

export const getAvailabilityResponseSchema = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      type: z.literal(API_TYPES.interviewAvailability),
      attributes: z.object({
        start: z.string(),
        end: z.string(),
        eventType: z.enum(["open-house", "interview-days"]).optional(),
      }),
    })
  ),
  links: z.object({
    next: z.string().optional(),
    prev: z.string().optional(),
  }),
});

export type GetAvailabilityResponse = z.infer<typeof getAvailabilityResponseSchema>;

export function transformInterviewAvailabilityData(
  interviewAvailability: GetAvailabilityResponse | undefined,
  workerTimezone: string,
  placementDetail: PlacementDetail
) {
  if (!interviewAvailability) {
    return [];
  }

  const conflictingTimeslots = placementDetail.interviews
    .filter((interview) => interview.attributes.status === InterviewStatus.BOOKED)
    .map((interview) => {
      return {
        start: interview.attributes.start,
        end: interview.attributes.end,
      };
    });

  return interviewAvailability.data
    .filter(
      (availability) =>
        !conflictingTimeslots.some(
          (timeslot) =>
            timeslot.start === availability.attributes.start &&
            timeslot.end === availability.attributes.end
        )
    )
    .map((availability) => {
      return {
        start: utcToZonedTime(availability.attributes.start, workerTimezone),
        end: utcToZonedTime(availability.attributes.end, workerTimezone),
        eventType: availability.attributes.eventType,
      };
    });
}

export type InterviewAvailability = ReturnType<typeof transformInterviewAvailabilityData>;

function fetchInterviewAvailabilityUrl(workplaceId: string) {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/interviews/availability`;
}

export function useGetInterviewAvailability(
  params: GetInterviewAvailabilityParams,
  queryParams?: GetInterviewAvailabilityQueryParams,
  options?: UseQueryOptions<GetAvailabilityResponse>
): UseQueryResult<GetAvailabilityResponse> {
  const { workplaceId } = params;
  const { filter } = queryParams ?? {};

  const history = useHistory();
  const cardType = getPlacementCardType(new URLSearchParams(history.location.search));
  return useQuery({
    queryKey: [
      `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/interviews/availability`,
      filter,
    ],
    queryFn: async () => {
      const [interviewDaysAvailability, openHouseAvailability] = await Promise.all([
        filter?.canScheduleInterviews
          ? get({
              url: fetchInterviewAvailabilityUrl(workplaceId),
              responseSchema: getAvailabilityResponseSchema,
              queryParams: {
                ...(filter && {
                  filter: {
                    ...(filter.workerId && { workerId: filter.workerId }),
                    ...(filter.placementId && { placementId: filter.placementId }),
                    ...(filter.excludedInterviewId && {
                      interviewId: { nin: filter.excludedInterviewId },
                    }),
                    ...(filter.start && { start: filter.start }),
                  },
                }),
              },
            })
          : Promise.resolve({
              data: {
                data: [],
                links: {
                  next: undefined,
                  prev: undefined,
                },
              },
            }),
        isDefined(filter?.hiringEventId) && !isEmpty(filter.hiringEventId)
          ? get({
              url: fetchInterviewAvailabilityUrl(workplaceId),
              responseSchema: getAvailabilityResponseSchema,
              queryParams: {
                ...(filter && {
                  filter: {
                    ...(filter.workerId && { workerId: filter.workerId }),
                    ...(filter.hiringEventId && { hiringEventId: filter.hiringEventId }),
                    ...(filter.placementId && { placementId: filter.placementId }),
                    ...(filter.excludedInterviewId && {
                      interviewId: { nin: filter.excludedInterviewId },
                    }),
                  },
                }),
              },
            })
          : Promise.resolve({
              data: {
                data: [],
                links: {
                  next: undefined,
                  prev: undefined,
                },
              },
            }),
      ]);

      const processedOpenHouseData = openHouseAvailability.data.data.map((openHouse) => ({
        ...openHouse,
        attributes: {
          ...openHouse.attributes,
          // If we are visiting the details page via open house card we want the open house day times to be shown as promoted,
          // otherwise we want to show the open house days as interview days
          // If the user is visiting via an sms link that does not have a card type, show open house days as promoted
          eventType:
            cardType !== "interview-days" &&
            isDefined(filter?.hiringEventId) &&
            !isEmpty(filter.hiringEventId)
              ? "open-house"
              : "interview-days",
        },
      }));

      const openHouseStartDateStrings = new Set(
        processedOpenHouseData.map((oh) => oh.attributes.start.slice(0, 12))
      );

      const filteredInterviewDaysData = interviewDaysAvailability.data.data
        .filter((interviewDay) => {
          // if we have interview day and open house on same day and hour ingore the interview day availability
          const interviewDayDateString = interviewDay.attributes.start.slice(0, 12);
          // Only include the interview day if no open house slot exists on the same calendar date
          return !openHouseStartDateStrings.has(interviewDayDateString);
        })
        .map((interviewDay) => ({
          ...interviewDay,
          attributes: {
            ...interviewDay.attributes,
            eventType: "interview-days",
          },
        }));

      // filter out open houses that are less than 2 hours from now
      const processedAndFilteredOpenHouseData = processedOpenHouseData.filter((openHouse) => {
        return isAfter(
          new Date(openHouse.attributes.start),
          isDefined(filter?.start?.gte)
            ? filter.start.gte
            : new Date(Date.now() + 2 * 60 * 60 * 1000)
        );
      });

      return {
        data: [...filteredInterviewDaysData, ...processedAndFilteredOpenHouseData].sort((a, b) => {
          return new Date(a.attributes.start).getTime() - new Date(b.attributes.start).getTime();
        }),
        links: {
          next: interviewDaysAvailability.data.links.next,
          prev: interviewDaysAvailability.data.links.prev,
        },
      };
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_INTERVIEW_AVAILABILITY_FAILURE,
    },
    enabled: options?.enabled ?? false,
    useErrorBoundary: false,
  });
}
